import { useEffect, useRef, useState } from 'react'
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { extractGoogleSheetText } from '../../../utils/fileExtractor'
import { TextInput } from '../../Common/TextInput'
import { Button } from '../../Common/Button'
import { useNls } from '../../../nls/nlsProvider'
import type { DataContext } from '../../../types/aiChatSource'

export interface GoogleSheetPaneProps {
  onAdd: (context: DataContext) => void
}

type FetchState =
  | { status: 'idle' }
  | { status: 'busy' }
  | { status: 'done'; text: string; url: string }
  | { status: 'error'; message: string }

const extractSheetId = (url: string) => {
  const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/)
  return match?.[1] ?? null
}

export const GoogleSheetPane = ({ onAdd }: GoogleSheetPaneProps) => {
  const { t } = useNls()
  const [url, setUrl] = useState('')
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'idle' })
  const debounceRef = useRef<number | null>(null)

  useEffect(() => {
    const trimmed = url.trim()
    if (!trimmed || !extractSheetId(trimmed)) {
      setFetchState((prev) => (prev.status === 'idle' ? prev : { status: 'idle' }))
      return
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(async () => {
      setFetchState({ status: 'busy' })
      try {
        const text = await extractGoogleSheetText(trimmed)
        setFetchState({ status: 'done', text, url: trimmed })
      } catch (err) {
        setFetchState({
          status: 'error',
          message: err instanceof Error ? err.message : t('NLSAiContextPickerFetchFailedMsg'),
        })
      }
    }, 500)
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current) }
  }, [url])

  const handleAttach = () => {
    if (fetchState.status !== 'done') return
    const sheetId = extractSheetId(fetchState.url)
    const context: DataContext = {
      id: `gsheet:${sheetId ?? Date.now()}`,
      kind: 'gsheet',
      label: `Google Sheet${sheetId ? ` (${sheetId.slice(0, 8)}…)` : ''}`,
      text: fetchState.text,
      fixedIngestCr: 3,
    }
    onAdd(context)
    setUrl('')
    setFetchState({ status: 'idle' })
  }

  const handleClear = () => {
    setUrl('')
    setFetchState({ status: 'idle' })
  }

  return (
    <div className="ctxPane ctxPane--gsheet">
      <div className="ctxPane__ingestHero">
        <GridOnRoundedIcon style={{ fontSize: 36, opacity: 0.5 }} />
        <h4>{t('NLSAiContextPickerCategoryGSheet')}</h4>
        <p>Paste a publicly-viewable Google Sheets URL. Line fetches the sheet as CSV.</p>
        <p className="ctxPane__costNote">+3 Cr fixed fetch fee per sheet</p>
      </div>

      <div className="ctxPane__urlRow">
        <TextInput
          type="url"
          className="ctxPane__urlInput"
          placeholder="https://docs.google.com/spreadsheets/d/…"
          value={url}
          onChange={setUrl}
          aria-label={t('NLSAiContextPickerCategoryGSheet')}
        />
      </div>

      {fetchState.status === 'busy' && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--busy">
          <div className="ctxPane__spinner" />
          <span>{t('NLSAiContextPickerFetchingSheet')}</span>
        </div>
      )}

      {fetchState.status === 'done' && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--done">
          <CheckCircleRoundedIcon style={{ color: 'var(--success)' }} />
          <div className="ctxPane__parseInfo">
            <strong>{t('NLSAiContextPickerSheetReady')}</strong>
            <small>{fetchState.text.length.toLocaleString()} chars extracted</small>
            <small className="ctxPane__textPreview">{fetchState.text.slice(0, 120).replace(/\n/g, ' ')}…</small>
          </div>
          <div className="ctxPane__parseActions">
            <Button size="sm" variant="primary" onClick={handleAttach}>
              {t('NLSAiContextPickerAttach')}
            </Button>
            <Button size="sm" variant="tertiary" onClick={handleClear}>
              {t('NLSAiContextPickerClear')}
            </Button>
          </div>
        </div>
      )}

      {fetchState.status === 'error' && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--error">
          <ErrorOutlineRoundedIcon style={{ color: 'var(--danger)' }} />
          <div className="ctxPane__parseInfo">
            <strong>{t('NLSAiContextPickerFetchFailed')}</strong>
            <small>{fetchState.message}</small>
          </div>
        </div>
      )}

      {fetchState.status === 'idle' && url.trim() && !extractSheetId(url.trim()) && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--error">
          <ErrorOutlineRoundedIcon style={{ color: 'var(--danger)' }} fontSize="small" />
          <small>{t('NLSAiContextPickerNotGSheetUrl')}</small>
        </div>
      )}
    </div>
  )
}
